import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CheckboxField,
  Field,
  PageHeader,
  SelectField,
  Skeleton,
  TextField,
  toast,
} from '@eon/ui';
import { resolveApiError, useTranslation } from '@eon/i18n';
import { hooks } from '@eon/api-client';
import type { Station } from '@eon/types';
import { ArrowLeft } from 'lucide-react';

const STATION_TYPE_VALUES = ['public', 'private', 'fleet', 'residential'] as const;
const STATION_STATUS_VALUES = ['active', 'inactive', 'maintenance', 'faulted'] as const;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Returns a valid org UUID or undefined (ignores placeholder / garbage). */
function normalizeOrganizationId(raw: string): string | undefined {
  const v = raw.trim();
  if (!v || v === '00000000-0000-0000-0000-000000000000') return undefined;
  return UUID_RE.test(v) ? v : undefined;
}

function stationToForm(station: Station) {
  const meta = station.metadata ?? {};
  const contact = station.contact ?? {};
  const hours = station.operatingHours ?? {};
  return {
    name: station.name ?? '',
    organizationId: station.organizationId ?? '',
    type: typeof meta.type === 'string' ? meta.type : 'public',
    status: typeof meta.status === 'string' ? meta.status : 'active',
    capacity: typeof meta.capacity === 'number' ? String(meta.capacity) : '1',
    installationDate:
      typeof meta.installationDate === 'string'
        ? meta.installationDate.slice(0, 10)
        : new Date().toISOString().slice(0, 10),
    description: station.description ?? '',
    displayOnMap: typeof meta.displayOnMap === 'boolean' ? meta.displayOnMap : true,
    address: station.address ?? '',
    latitude: station.latitude != null ? String(station.latitude) : '0',
    longitude: station.longitude != null ? String(station.longitude) : '0',
    city: station.city ?? '',
    country: station.country ?? '',
    contactEmail: typeof contact.email === 'string' ? contact.email : '',
    contactPhone: typeof contact.phone === 'string' ? contact.phone : '',
    contactWebsite: typeof contact.website === 'string' ? contact.website : '',
    is24Hours: Boolean(hours.is24Hours ?? true),
    isPublic: station.isPublic ?? false,
    isActive: station.isActive ?? station.active ?? true,
  };
}

const defaultForm = () => ({
  name: '',
  organizationId: '',
  type: 'public',
  status: 'active',
  capacity: '1',
  installationDate: new Date().toISOString().slice(0, 10),
  description: '',
  displayOnMap: true,
  address: '',
  latitude: '0',
  longitude: '0',
  city: '',
  country: '',
  contactEmail: '',
  contactPhone: '',
  contactWebsite: '',
  is24Hours: true,
  isPublic: false,
  isActive: true,
});

export function StationNewPage() {
  return <StationEditForm />;
}

export function StationEditPage() {
  const { stationId } = useParams({ from: '/_app/stations/$stationId/edit' });
  return <StationEditForm stationId={stationId} />;
}

function StationEditForm({ stationId }: { stationId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isNew = !stationId;

  const { data: station, isLoading } = hooks.useAdminStation(stationId);
  const groups = hooks.useAdminGroups({ page: 1, pageSize: 200 });
  const create = hooks.useCreateStation();
  const update = hooks.useUpdateStation();
  const [form, setForm] = useState(defaultForm);

  const organizationOptions = (groups.data?.data ?? []).map((g) => ({
    value: g.id,
    label: g.name,
  }));

  const stationTypeOptions = STATION_TYPE_VALUES.map((value) => ({
    value,
    label: t(`station.type.${value}`),
  }));

  const stationStatusOptions = STATION_STATUS_VALUES.map((value) => ({
    value,
    label: t(`station.metaStatus.${value}`),
  }));

  useEffect(() => {
    if (station) setForm(stationToForm(station));
  }, [station]);

  const set = <K extends keyof ReturnType<typeof defaultForm>>(
    k: K,
    v: ReturnType<typeof defaultForm>[K],
  ) => setForm((s) => ({ ...s, [k]: v }));

  const buildPayload = () => {
    const latitude = Number.parseFloat(form.latitude);
    const longitude = Number.parseFloat(form.longitude);
    const capacity = Number.parseInt(form.capacity, 10);
    const contact: Record<string, unknown> = {};
    if (form.contactEmail.trim()) contact.email = form.contactEmail.trim();
    if (form.contactPhone.trim()) contact.phone = form.contactPhone.trim();
    if (form.contactWebsite.trim()) contact.website = form.contactWebsite.trim();
    const organizationId = normalizeOrganizationId(form.organizationId);
    return {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      organizationId,
      address: form.address.trim(),
      city: form.city.trim() || undefined,
      country: form.country.trim() || undefined,
      latitude,
      longitude,
      type: form.type,
      status: form.status,
      capacity: Number.isFinite(capacity) ? capacity : 1,
      installationDate: form.installationDate,
      displayOnMap: form.displayOnMap,
      isPublic: form.isPublic || form.type === 'public',
      isActive: form.isActive,
      contact: Object.keys(contact).length > 0 ? contact : undefined,
      operatingHours: { is24Hours: form.is24Hours },
    };
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t('station.create.nameRequired'));
      return;
    }
    if (!form.address.trim()) {
      toast.error(t('station.create.addressRequired'));
      return;
    }
    if (!normalizeOrganizationId(form.organizationId)) {
      toast.error(t('station.create.organizationRequired'));
      return;
    }
    const latitude = Number.parseFloat(form.latitude);
    const longitude = Number.parseFloat(form.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      toast.error(t('station.create.coordsRequired'));
      return;
    }
    try {
      const payload = buildPayload();
      if (isNew) {
        const created = await create.mutateAsync(payload);
        toast.success(t('station.create.success'));
        navigate({ to: '/stations/$stationId', params: { stationId: created.id } });
      } else if (stationId) {
        await update.mutateAsync({ id: stationId, patch: payload });
        toast.success(t('station.edit.success'));
        navigate({ to: '/stations/$stationId', params: { stationId } });
      }
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  const busy = create.isPending || update.isPending;

  if (!isNew && isLoading) {
    return (
      <>
        <PageHeader title={t('station.edit.title')} />
        <Skeleton className="h-96 w-full" />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={isNew ? t('station.create.title') : t('station.edit.title')}
        description={isNew ? t('station.create.description') : station?.name}
        actions={
          <Button variant="outline" asChild>
            <Link to={isNew ? '/stations' : '/stations/$stationId'} params={stationId ? { stationId } : undefined}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('station.create.back')}
            </Link>
          </Button>
        }
      />

      <Card className="max-w-4xl">
        <CardHeader>
          <CardTitle className="text-base">{t('station.create.formTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('station.name')} required>
              <TextField value={form.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field
              label={t('station.detail.organization')}
              required
              hint={t('station.create.organizationHint')}
            >
              <SelectField
                value={form.organizationId || undefined}
                onValueChange={(v) => set('organizationId', v)}
                options={organizationOptions}
                placeholder={t('station.create.selectOrganization')}
                disabled={groups.isLoading}
              />
            </Field>
            <Field label={t('station.detail.type')} required>
              <SelectField value={form.type} onValueChange={(v) => set('type', v)} options={stationTypeOptions} />
            </Field>
            <Field label={t('station.status')} required>
              <SelectField
                value={form.status}
                onValueChange={(v) => set('status', v)}
                options={stationStatusOptions}
              />
            </Field>
            <Field label={t('station.detail.capacity')} required>
              <TextField
                type="number"
                min={1}
                value={form.capacity}
                onChange={(e) => set('capacity', e.target.value)}
              />
            </Field>
            <Field label={t('station.detail.installationDate')} required>
              <TextField
                type="date"
                value={form.installationDate}
                onChange={(e) => set('installationDate', e.target.value)}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t('station.create.descriptionField')}>
                <TextField value={form.description} onChange={(e) => set('description', e.target.value)} />
              </Field>
            </div>
            <CheckboxField
              checked={form.displayOnMap}
              onCheckedChange={(c) => set('displayOnMap', c)}
              label={t('station.detail.displayOnMap')}
            />
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('station.detail.location')}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label={t('station.address')} required>
                  <TextField value={form.address} onChange={(e) => set('address', e.target.value)} />
                </Field>
              </div>
              <Field label={t('station.detail.city')}>
                <TextField value={form.city} onChange={(e) => set('city', e.target.value)} />
              </Field>
              <Field label={t('station.detail.country')}>
                <TextField value={form.country} onChange={(e) => set('country', e.target.value)} />
              </Field>
              <Field label={t('station.detail.latitude')} required>
                <TextField
                  value={form.latitude}
                  onChange={(e) => set('latitude', e.target.value)}
                  className="font-mono"
                />
              </Field>
              <Field label={t('station.detail.longitude')} required>
                <TextField
                  value={form.longitude}
                  onChange={(e) => set('longitude', e.target.value)}
                  className="font-mono"
                />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('station.detail.contact')}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t('user.email')}>
                <TextField
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => set('contactEmail', e.target.value)}
                />
              </Field>
              <Field label={t('station.detail.phone')}>
                <TextField value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} />
              </Field>
              <div className="sm:col-span-2">
                <Field label={t('station.detail.website')}>
                  <TextField
                    value={form.contactWebsite}
                    onChange={(e) => set('contactWebsite', e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </section>

          <section className="flex flex-wrap gap-4">
            <CheckboxField
              checked={form.is24Hours}
              onCheckedChange={(c) => set('is24Hours', c)}
              label={t('station.detail.is24Hours')}
            />
            <CheckboxField
              checked={form.isPublic}
              onCheckedChange={(c) => set('isPublic', c)}
              label={t('station.detail.isPublic')}
            />
            <CheckboxField
              checked={form.isActive}
              onCheckedChange={(c) => set('isActive', c)}
              label={t('station.detail.isActive')}
            />
          </section>
        </CardContent>
        <CardFooter className="justify-end gap-2 border-t pt-4">
          <Button variant="outline" asChild disabled={busy}>
            <Link to="/stations">{t('actions.cancel')}</Link>
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy
              ? t('station.create.saving')
              : isNew
                ? t('station.create.submit')
                : t('station.edit.submit')}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}
